import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Ban, X } from 'lucide-react';
import { supabase } from 'lib/supabase';

const ITALIAN_MONTHS = [
  'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
  'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'
];

function getNextBillingDate() {
  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return `1 ${ITALIAN_MONTHS?.[nextMonth?.getMonth()]} ${nextMonth?.getFullYear()}`;
}

const PROMO_BANNER_KEY = 'volaptic_promo_banner_dismissed';

const BillingStatusBanner = ({ billingExempt = false }) => {
  const navigate = useNavigate();
  const [bannerData, setBannerData] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(PROMO_BANNER_KEY) === 'true'
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) return;

        const [
          userRes,
          unbilledRes,
          feeCentsRes,
          minThresholdRes,
          invoiceRes
        ] = await Promise.all([
          supabase?.from('users')?.select('billing_status, free_quotes_remaining')?.eq('id', user?.id)?.single(),
          supabase?.from('quotes')?.select('id', { count: 'exact', head: true })?.eq('professional_id', user?.id)?.eq('status', 'accepted')?.eq('billed_via_promo', false)?.eq('fee_billed', false),
          supabase?.from('platform_config')?.select('value')?.eq('key', 'quote_fee_cents')?.single(),
          supabase?.from('platform_config')?.select('value')?.eq('key', 'billing_min_threshold_cents')?.single(),
          supabase?.from('professional_invoices')?.select('amount_cents, status, failure_reason, period_year, period_month')?.eq('professional_id', user?.id)?.in('status', ['failed', 'pending'])?.order('created_at', { ascending: false })?.limit(1)
        ]);

        const billingStatus = userRes?.data?.billing_status;
        const freeQuotesRemaining = userRes?.data?.free_quotes_remaining ?? 0;
        const N = unbilledRes?.count ?? 0;
        const feeCents = parseInt(feeCentsRes?.data?.value ?? '99', 10);
        const minThresholdCents = parseInt(minThresholdRes?.data?.value ?? '500', 10);
        const invoice = invoiceRes?.data?.[0] ?? null;

        const billableAmount = (N * feeCents) / 100;
        const minThreshold = minThresholdCents / 100;
        const belowThreshold = billableAmount * 100 < minThresholdCents;
        const nextBillingDate = getNextBillingDate();

        setBannerData({
          billingStatus,
          freeQuotesRemaining,
          N,
          feeCents,
          billableAmount,
          minThreshold,
          belowThreshold,
          nextBillingDate,
          invoice,
        });
      } catch (err) {
        console.error('BillingStatusBanner fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBillingData();
  }, []);

  if (loading || !bannerData) return null;

  const {
    billingStatus,
    freeQuotesRemaining,
    N,
    billableAmount,
    minThreshold,
    belowThreshold,
    nextBillingDate,
    invoice,
  } = bannerData;

  // Determine which variant to show
  let variant = null;

  if (billingStatus === 'blocked') {
    variant = 5;
  } else if (billingStatus === 'past_due') {
    variant = 4;
  } else if (billingStatus === 'active') {
    if (N > 0 && !belowThreshold) {
      variant = 1;
    } else if (N > 0 && belowThreshold) {
      variant = 2;
    } else if (N === 0 && freeQuotesRemaining > 0) {
      // Variant 3 = free quotes promo banner — hidden for VIP/billing-exempt users
      if (billingExempt) return null;
      variant = 3;
    } else {
      // N === 0 && freeQuotesRemaining === 0 → no banner
      return null;
    }
  } else {
    return null;
  }

  // Dismissible only for variants 1, 2, 3
  const isDismissible = variant === 1 || variant === 2 || variant === 3;
  if (isDismissible && dismissed) return null;

  const amountStr = (n) => n?.toFixed(2)?.replace('.', ',');

  // Variant styles
  const styles = {
    1: { bg: '#e6f4fb', border: '#104d98' },
    2: { bg: '#e6f4fb', border: '#104d98' },
    3: { bg: '#ecfdf5', border: '#059669' },
    4: { bg: '#fef3c7', border: '#f59e0b' },
    5: { bg: '#fee2e2', border: '#dc2626' },
  };

  const { bg, border } = styles?.[variant];

  const invoiceAmount = invoice ? (invoice?.amount_cents / 100)?.toFixed(2)?.replace('.', ',') : '0,00';
  const periodMonth = invoice?.period_month ?? '';
  const periodYear = invoice?.period_year ?? '';
  const failureReason = invoice?.failure_reason || 'pagamento non riuscito';

  return (
    <div
      style={{
        background: bg,
        borderLeft: `4px solid ${border}`,
        borderRadius: '8px',
        padding: '14px 18px',
        marginBottom: '20px',
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
      }}
    >
      {/* Icon for variants 4 and 5 */}
      {variant === 4 && (
        <AlertTriangle size={20} style={{ color: '#f59e0b', flexShrink: 0, marginTop: '2px' }} />
      )}
      {variant === 5 && (
        <Ban size={20} style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
      )}

      <div style={{ flex: 1 }}>
        {/* Variant 1 */}
        {variant === 1 && (
          <p style={{ margin: 0, fontSize: '14px', color: '#1e3a5f', lineHeight: '1.5' }}>
            Hai <strong>{N}</strong> preventivi accettati questo mese (
            <strong>€{amountStr(billableAmount)}</strong> da addebitare il {nextBillingDate})
          </p>
        )}

        {/* Variant 2 */}
        {variant === 2 && (
          <p style={{ margin: 0, fontSize: '14px', color: '#1e3a5f', lineHeight: '1.5' }}>
            Hai <strong>{N}</strong> preventivi accettati questo mese (
            <strong>€{amountStr(billableAmount)}</strong> accumulati). Importo sotto la soglia minima di{' '}
            <strong>€{amountStr(minThreshold)}</strong> — sarà sommato al mese successivo.
          </p>
        )}

        {/* Variant 3 */}
        {variant === 3 && (
          <p style={{ margin: 0, fontSize: '14px', color: '#065f46', lineHeight: '1.5' }}>
            Hai <strong>{freeQuotesRemaining}</strong> preventivi gratuiti rimanenti questo mese (promo lancio).
          </p>
        )}

        {/* Variant 4 */}
        {variant === 4 && (
          <>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#78350f', lineHeight: '1.5' }}>
              Pagamento fallito per la fatturazione di <strong>{periodMonth}/{periodYear}</strong> (€{invoiceAmount}).
              Motivo: <strong>{failureReason}</strong>. Ritenteremo automaticamente l'addebito.
              Aggiorna il metodo di pagamento per evitare il blocco.
            </p>
            <button
              onClick={() => navigate('/professional/settings')}
              style={{
                background: '#f59e0b',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '7px 16px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Aggiorna metodo di pagamento
            </button>
          </>
        )}

        {/* Variant 5 */}
        {variant === 5 && (
          <>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#7f1d1d', lineHeight: '1.5' }}>
              Account in blocco soft: non puoi inviare nuovi preventivi finché non viene saldato l'importo dovuto di{' '}
              <strong>€{invoiceAmount}</strong>. Aggiorna il metodo di pagamento per sbloccare.
            </p>
            <button
              onClick={() => navigate('/professional/settings')}
              style={{
                background: '#dc2626',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '7px 16px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Aggiorna metodo di pagamento
            </button>
          </>
        )}
      </div>

      {/* Dismiss button for variants 1, 2, 3 */}
      {isDismissible && (
        <button
          onClick={() => {
            if (variant === 3) {
              localStorage.setItem(PROMO_BANNER_KEY, 'true');
            }
            setDismissed(true);
          }}
          aria-label="Chiudi"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px',
            color: '#6b7280',
            flexShrink: 0,
            marginTop: '1px',
          }}
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
};

export default BillingStatusBanner;
